<template>
  <div class="sidebar" :class="{ 'sidebar--collapsed': !open }">
    <div ref="innerRef" class="sidebar__inner">
      <div class="sidebar__context">
        <ContextSelector :context="nav" :contexts="contexts" @select-context="(id) => emit('select-context', id)" />
      </div>

      <Accordion
        :value="openSections"
        multiple
        class="sidebar__sections"
        @update:value="onSectionsChange"
      >
        <AccordionPanel v-for="section in nav.sections" :key="section.id" :value="section.id">
          <AccordionHeader>
            <span class="section__header">
              <span class="section__label">{{ section.label }}</span>
              <span v-if="section.description" class="section__desc">{{ section.description }}</span>
            </span>
          </AccordionHeader>
          <AccordionContent>
            <div class="section__pages">
              <NavItem
                v-for="page in section.pages"
                :key="page.id"
                :page="page"
                :active="activePage === page.id"
                @select="(id) => emit('navigate', id)"
              />
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue'
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import ContextSelector from './ContextSelector.vue'
import NavItem from './NavItem.vue'

// The sidebar: the context switcher and the collapsible sections. Section
// open/closed state persists per context under
// `administration-sections-{nav.id}` as a `{sectionId: bool}` map. Smart-init:
// if all-expanded would overflow the available height, open only the first.
//
//
// vibe-portal's standalone Dashboard row is gone with the `showDashboard` prop:
// this app registers no dashboard route, so the row could only emit an intent
// the host resolves to nothing.
defineOptions({ name: 'ShellSidebar' })

const props = defineProps({
  nav: { type: Object, required: true },
  contexts: { type: Array, default: () => [] },
  activePage: { type: String, default: '' },
  open: { type: Boolean, default: true },
})
const emit = defineEmits(['navigate', 'select-context'])

const innerRef = ref(null)
const openSections = ref(props.nav.sections.map((section) => section.id)) // optimistic: all open until init

function storageKey() {
  return `administration-sections-${props.nav.id}`
}

function mapToOpenIds(map) {
  return props.nav.sections.filter((section) => map[section.id]).map((section) => section.id)
}

function readStoredMap() {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey()))
    return value && typeof value === 'object' ? value : null
  } catch {
    return null
  }
}

function persist(openIds) {
  const map = Object.fromEntries(props.nav.sections.map((section) => [section.id, openIds.includes(section.id)]))
  localStorage.setItem(storageKey(), JSON.stringify(map))
}

function onSectionsChange(next) {
  openSections.value = next
  persist(next)
}

onMounted(async () => {
  const stored = readStoredMap()
  if (stored) {
    openSections.value = mapToOpenIds(stored)
    return
  }
  // Smart init — measure available height vs. estimated all-expanded height.
  await nextTick()
  const availableHeight = innerRef.value?.clientHeight ?? 0
  const estimatedHeight =
    80 + props.nav.sections.reduce((sum, section) => sum + 44 + section.pages.length * 36, 0)
  const openIds =
    estimatedHeight <= availableHeight
      ? props.nav.sections.map((section) => section.id)
      : props.nav.sections.slice(0, 1).map((section) => section.id)
  openSections.value = openIds
  persist(openIds)
})
</script>

<style scoped>
.sidebar {
  width: 240px;
  flex-shrink: 0;
  overflow: hidden;
  background: var(--p-content-background);
  border-right: 1px solid var(--p-content-border-color);
  transition: width 220ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar--collapsed {
  width: 0;
  border-right-color: transparent;
}


.sidebar__inner {
  width: 240px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.sidebar__context {
  padding: 14px 14px 12px;
  border-bottom: 1px solid var(--p-content-border-color);
}


/* ── Section header (Accordion) ────────────────────────────────────────── */
.section__header {
  display: flex;
  align-items: baseline;
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;
}

.section__label {
  font-size: 13px;
  font-weight: 800;
  color: var(--p-text-color);
  letter-spacing: -0.01em;
  white-space: nowrap;
  flex-shrink: 0;
}

.section__desc {
  font-size: 10px;
  color: var(--p-surface-400);
  font-weight: 500;
  margin-left: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section__pages {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

/* Strip PrimeVue's default Accordion styling so it reads as a nav. */
.sidebar__sections :deep(.p-accordionpanel) {
  border: 0;
  border-bottom: 1px solid var(--p-content-border-color);
}

.sidebar__sections :deep(.p-accordionheader) {
  padding: 10px 14px 8px;
  background: transparent;
  color: var(--p-text-color);
}

.sidebar__sections :deep(.p-accordionheader:hover) {
  background: var(--p-content-hover-background);
}

.sidebar__sections :deep(.p-accordioncontent-content) {
  padding: 0 10px 8px;
  background: transparent;
  border: 0;
}
</style>
